import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MinusIcon, PlusIcon, Trash2Icon, ShoppingBagIcon, StoreIcon, CheckIcon, ClockIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { money } from '../../data/food';

/**
 * The order in progress. Kept as a slide-over rather than a page so nobody
 * loses the results they were browsing, and grouped by merchant because each
 * merchant fulfils its own order.
 */
export function CartDrawer() {
  const {
    open,
    setOpen,
    byMerchant,
    count,
    total,
    update,
    remove,
    clear,
    placeOrders
  } = useCart();
  const navigate = useNavigate();
  const [placed, setPlaced] = useState(0);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, setOpen]);

  // A fresh visit to the cart is never a confirmation screen.
  useEffect(() => {
    if (open) setPlaced(0);
  }, [open]);
  return createPortal(<AnimatePresence data-id="element-909" data-mp="1xvfcxd4ifjv">
      {open && <div className="fixed inset-0 z-[90] flex justify-end" data-id="element-910" data-mp="1ywq10q89012">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-id="element-911" data-mp="6w6pl97bys2m" />
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} aria-label="Your order" className="relative w-full sm:w-[420px] h-full bg-white shadow-large flex flex-col" data-id="element-912" data-mp="2hn6inid0o99s">
            <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 flex-shrink-0" data-id="element-913" data-mp="4jmcaq8elgw6">
              <div data-id="element-914" data-mp="foeciutec4p">
                <h2 className="font-display font-bold text-lg text-dark-navy" data-id="element-915" data-mp="jca7onqfaml">
                  Your order
                </h2>
                <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-916" data-mp="3mopmgpd88j3g">
                  {count === 0 ? 'Nothing added yet' : `${count} item${count === 1 ? '' : 's'} · ${byMerchant.length} ${byMerchant.length === 1 ? 'place' : 'places'}`}
                </p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-917" data-mp="1sh3y3l7yu8j5">
                <XIcon className="w-4 h-4" data-id="element-918" data-mp="275i0jv1gzuz6" />
              </button>
            </header>

            {placed > 0 ? <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-id="element-919" data-mp="19irndv1eoqlg">
                <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center" data-id="element-920" data-mp="1ehcd30j17rvn">
                  <CheckIcon className="w-7 h-7 text-success" data-id="element-921" data-mp="1jeulmpt50534" />
                </div>
                <h3 className="font-display font-bold text-lg text-dark-navy mt-4" data-id="element-922" data-mp="1ofs3t2e5cz4m">
                  Order placed
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-2" data-id="element-923" data-mp="2uwx6dxsgguf6">
                  {placed === 1 ? 'The seller has it. You can follow its progress any time in My Orders.' : `Sent as ${placed} separate orders, one to each seller. You can follow them all in My Orders.`}
                </p>
                <button onClick={() => {
            setOpen(false);
            navigate('/orders');
          }} className="mt-6 px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-924" data-mp="37dy6ihpve96u">
                  View my orders
                </button>
                <button onClick={() => setOpen(false)} className="mt-3 text-sm font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-925" data-mp="3q4fw4dfeoonz">
                  Keep browsing
                </button>
              </div> : count === 0 ? <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-id="element-926" data-mp="3v4aludng387g">
                <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center" data-id="element-927" data-mp="ikq4ez010mqi">
                  <ShoppingBagIcon className="w-6 h-6 text-slate-300" data-id="element-928" data-mp="mjp86ga0udgd" />
                </div>
                <h3 className="font-display font-bold text-base text-dark-navy mt-4" data-id="element-929" data-mp="8kfov7qir386">
                  Your order is empty
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-1.5" data-id="element-930" data-mp="dm53sygv411j">
                  Ask Talab for something to eat, then choose your options on
                  any result to add it here.
                </p>
              </div> : <>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-id="element-931" data-mp="3m1yhl5jt6yew">
                  {byMerchant.map(group => <section key={group.merchant} data-id="element-932" data-mp="3c1xvxcygw67j">
                      <div className="flex items-center gap-2 mb-3" data-id="element-933" data-mp="3c38yxak399xl">
                        <StoreIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-934" data-mp="3h1k8dk6h1ifa" />
                        <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-500 truncate" data-id="element-935" data-mp="2xwfhm4ywah2t">
                          {group.merchant}
                        </h3>
                        <span className="ml-auto text-[12px] font-bold text-slate-400 flex-shrink-0" data-id="element-936" data-mp="2t3metmmzlitd">
                          {money(group.total)}
                        </span>
                      </div>

                      <div className="space-y-2.5" data-id="element-937" data-mp="2j5ord1cybq1t">
                        {group.lines.map(line => <div key={line.id} className="flex gap-3 p-3 border border-slate-200 rounded-2xl" data-id="element-938" data-mp="3bw6zigsj8hm3">
                            <img src={line.image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-slate-100" data-id="element-939" data-mp="31y11495xptlz" />
                            <div className="flex-1 min-w-0" data-id="element-940" data-mp="1qi2mlprzmfdd">
                              <div className="flex items-start justify-between gap-2" data-id="element-941" data-mp="6137zz7ri13u">
                                <p className="text-sm font-bold text-dark-navy leading-snug" data-id="element-942" data-mp="azwayxpfa9pr">
                                  {line.title}
                                </p>
                                <button onClick={() => remove(line.id)} aria-label={`Remove ${line.title}`} className="text-slate-300 hover:text-error transition-colors duration-150 ease-out flex-shrink-0" data-id="element-943" data-mp="fwwf332pp2e8">
                                  <Trash2Icon className="w-4 h-4" data-id="element-944" data-mp="yoz34zhonkyi" />
                                </button>
                              </div>
                              <p className="text-[12px] text-slate-500 leading-snug mt-1" data-id="element-945" data-mp="13nsfupp5b7xm">
                                {line.summary}
                              </p>
                              <div className="flex items-center justify-between gap-2 mt-2.5" data-id="element-946" data-mp="35gztlup41hhe">
                                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-id="element-947" data-mp="1b71d5kkdng6b">
                                  <button onClick={() => update(line.id, line.qty - 1)} aria-label="One fewer" className="w-7 h-7 rounded-md flex items-center justify-center text-slate-600 hover:bg-white transition-colors duration-150 ease-out" data-id="element-948" data-mp="167yld1ter6ga">
                                    <MinusIcon className="w-3.5 h-3.5" data-id="element-949" data-mp="1274vw40bhjla" />
                                  </button>
                                  <span className="w-5 text-center text-[13px] font-bold text-dark-navy" data-id="element-950" data-mp="x7s6bbi29yf0">
                                    {line.qty}
                                  </span>
                                  <button onClick={() => update(line.id, line.qty + 1)} aria-label="One more" className="w-7 h-7 rounded-md flex items-center justify-center text-slate-600 hover:bg-white transition-colors duration-150 ease-out" data-id="element-951" data-mp="28o030watafir">
                                    <PlusIcon className="w-3.5 h-3.5" data-id="element-952" data-mp="2b9ziawkzhedn" />
                                  </button>
                                </div>
                                <span className="text-sm font-bold text-dark-navy" data-id="element-953" data-mp="33vitxf9i0ft5">
                                  {money(line.unitPrice * line.qty)}
                                </span>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </section>)}

                  <button onClick={clear} className="text-[12px] font-semibold text-slate-400 hover:text-error transition-colors duration-150 ease-out" data-id="element-954" data-mp="3mnbm57gqtwzt">
                    Clear order
                  </button>
                </div>

                <div className="px-5 py-4 border-t border-slate-100 bg-white flex-shrink-0 space-y-3" data-id="element-955" data-mp="1knaqeeg21o8h">
                  <div className="flex items-center justify-between" data-id="element-956" data-mp="1pl2ojq7am6l4">
                    <span className="text-sm font-semibold text-slate-500" data-id="element-957" data-mp="1buh5o1hu9wn8">
                      Total
                    </span>
                    <span className="font-display font-bold text-xl text-dark-navy" data-id="element-958" data-mp="1glfq9d9v1gwa">
                      {money(total)}
                    </span>
                  </div>
                  {byMerchant.length > 1 && <p className="flex items-start gap-2 text-[12px] text-slate-500 leading-relaxed" data-id="element-959" data-mp="16w5sbgsc5409">
                      <ClockIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-960" data-mp="sw4vqdj3siql" />
                      This goes to {byMerchant.length} kitchens as separate
                      orders, so they may arrive at different times.
                    </p>}
                  <button onClick={() => setPlaced(placeOrders().length)} className="w-full px-5 py-3.5 bg-primary-indigo text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-961" data-mp="9auuu8xv5fu6">
                    Place order · {money(total)}
                  </button>
                </div>
              </>}
          </motion.aside>
        </div>}
    </AnimatePresence>, document.body);
}